import { z } from 'zod';
import { IDENTIFIER_TYPES } from '../constants/identity.constant';
import { describePhoneRejection, normalisePhone } from '../utils/phone.util';
import { normaliseEmail } from '../utils/identifier.util';

/**
 * The identity primitives every other schema builds on.
 *
 * `phoneSchema` is a `.transform`, not a `.refine`, and that is the point: the value
 * that reaches `res.locals.validated` — and therefore the value a service can possibly
 * write to a model — is ALREADY canonical. There is no path by which a raw phone
 * reaches the database, because parsing is the normalisation.
 *
 * The rejection text comes from `describePhoneRejection`, so the message an officer
 * sees in the browser and the message the API returns in a 422 are the same string
 * produced by the same function.
 */
export const phoneSchema = z
  .string()
  .max(32, 'Phone number is too long')
  .transform((raw, ctx) => {
    const result = normalisePhone(raw);
    if (!result.ok) {
      ctx.addIssue({ code: 'custom', message: describePhoneRejection(result) });
      return z.NEVER;
    }
    return result.phone;
  });

/**
 * Trimmed and lowercased on the way in, because the unique partial index is on the
 * stored value: if zod and mongoose disagree about case, the index protects a string
 * validation never saw.
 */
export const emailSchema = z
  .string()
  .trim()
  .max(254)
  .transform((raw) => normaliseEmail(raw))
  .pipe(z.email('Enter a valid email address'));

/**
 * The single "Phone or email" login field. One input, never a toggle (FR-5.8.4).
 *
 * ⚠️ [amended P4-UX.1] IT NOW VALIDATES. IT USED TO BE A PASSTHROUGH, AND THAT WAS THE
 * WHOLE REASON A NINE-DIGIT NUMBER SAID "Request validation failed".
 *
 * `z.string().trim().min(1).max(254)` accepts `987654321` — MEASURED — so the rule
 * `useZodForm` derives from this schema could never fire, the route-level `validate`
 * middleware passed it through, and the first thing that objected was `resolveIdentifier`
 * deep in the service. Its rejection is a `ValidationError` whose useful sentence lives in
 * `details[0].message` while the envelope's top-level `message` is the generic one — so the
 * operator got the generic one.
 *
 * Validating here fixes it at the source: the login form now refuses `987654321` with
 * "That reduces to 9 digits. A mobile number is 10 digits." BEFORE any request is sent,
 * which is PAMS_CONVENTIONS §6's rule ("frontend rules derive from the shared schema")
 * doing the work it was always supposed to.
 *
 * ⚠️ IT VALIDATES BUT DOES NOT TRANSFORM. The service normalises and keys the rate limiter
 * on the CANONICAL identity (§10's whole argument for counting after normalisation), so it
 * must keep receiving what the operator typed. Returning a normalised value here would move
 * that decision to the edge and silently change what the counter counts.
 */
export const identifierSchema = z
  .string()
  .trim()
  .min(1, 'Enter your phone number or email address')
  .max(254)
  .superRefine((value, ctx) => {
    // An `@` is the only thing that makes this an email rather than a mistyped number.
    if (value.includes('@')) {
      if (!z.email().safeParse(normaliseEmail(value)).success) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid email address.' });
      }
      return;
    }
    const result = normalisePhone(value);
    if (!result.ok) ctx.addIssue({ code: 'custom', message: describePhoneRejection(result) });
  });

export const identifierTypeSchema = z.enum(IDENTIFIER_TYPES);

/**
 * A name as typed by a human. Internal whitespace is collapsed and the ends trimmed,
 * but CASE IS LEFT ALONE — normalising it is destructive (FR-5.1.6e), and a system that
 * renders an officer's name differently from how their unit writes it is a system
 * people stop trusting.
 */
export const personNameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(160, 'Name is too long')
  .transform((raw) => raw.trim().replace(/\s+/g, ' '))
  .refine((value) => value.length > 0, 'Name is required');
